import React, { useState, useEffect } from 'react';
import {
  getContactMessages,
  getContactMessage,
  updateContactMessageStatus,
  deleteContactMessage,
  sendReplyToContact,
} from '../services/apiService';
import Toast from './Toast';
import ConfirmDialog from './ConfirmDialog';

const ContactMessages = () => {
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, read, replied
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDetail, setShowDetail] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [replyContent, setReplyContent] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  // 显示 Toast 提示
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // 加载消息列表
  const loadMessages = async () => {
    setIsLoading(true);
    try {
      const statusParam = filter === 'all' ? undefined : filter;
      const result = await getContactMessages(currentPage, 20, statusParam);

      if (result.success) {
        setMessages(result.messages);
        setTotalPages(result.pagination.pages);
      }
    } catch (error) {
      console.error('加载消息失败:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
  }, [currentPage, filter]);

  // 查看消息详情
  const handleViewMessage = async (id) => {
    try {
      const result = await getContactMessage(id);
      if (result.success) {
        setSelectedMessage(result.message);
        setAdminNotes(result.message.admin_notes || '');
        setReplyContent(''); // 清空上次的回复内容
        setAttachments([]); // 清空附件
        setShowDetail(true);
        // 刷新列表（因为状态可能已更新为已读）
        loadMessages();
      }
    } catch (error) {
      console.error('获取消息详情失败:', error);
    }
  };

  // 更新消息状态
  const handleUpdateStatus = async (status) => {
    if (!selectedMessage) return;

    try {
      const result = await updateContactMessageStatus(
        selectedMessage.id,
        status,
        adminNotes
      );

      if (result.success) {
        setSelectedMessage(result.data);
        loadMessages();
        showToast('状态更新成功', 'success');
      } else {
        showToast(result.message || '更新失败', 'error');
      }
    } catch (error) {
      console.error('更新状态失败:', error);
      showToast('更新失败，请稍后重试', 'error');
    }
  };

  // 删除消息
  const handleDelete = async (id) => {
    setConfirmDialog({
      message: '确定要删除这条消息吗？删除后将无法恢复。',
      type: 'danger',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          const result = await deleteContactMessage(id);
          if (result.success) {
            setShowDetail(false);
            setSelectedMessage(null);
            loadMessages();
            showToast('消息已删除', 'success');
          } else {
            showToast(result.message || '删除失败', 'error');
          }
        } catch (error) {
          console.error('删除失败:', error);
          showToast('删除失败，请稍后重试', 'error');
        }
      },
      onCancel: () => setConfirmDialog(null),
    });
  };

  // 发送回复邮件
  const handleSendReply = async () => {
    if (!selectedMessage) return;

    if (!replyContent || replyContent.trim() === '') {
      showToast('请输入回复内容', 'warning');
      return;
    }

    setIsSending(true);
    try {
      const result = await sendReplyToContact(
        selectedMessage.id,
        replyContent,
        attachments
      );

      if (result.success) {
        showToast('回复已成功发送！', 'success');
        setReplyContent('');
        setAttachments([]);
        // 刷新消息列表和详情
        await handleViewMessage(selectedMessage.id);
      } else {
        showToast(result.message || '发送失败', 'error');
      }
    } catch (error) {
      console.error('发送回复失败:', error);
      showToast('发送失败，请稍后重试', 'error');
    } finally {
      setIsSending(false);
    }
  };

  // 处理文件选择
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);

    // 验证文件数量
    if (attachments.length + files.length > 5) {
      showToast('最多只能上传5个附件', 'warning');
      return;
    }

    // 验证文件大小（10MB）
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        showToast(`文件 ${file.name} 超过 10MB 限制`, 'warning');
        return;
      }
    }

    setAttachments([...attachments, ...files]);
  };

  // 删除附件
  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  // 格式化日期
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 获取状态标签
  const getStatusBadge = (status) => {
    const badges = {
      unread: {
        text: '未读',
        color: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
      },
      read: {
        text: '已读',
        color: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
      },
      replied: {
        text: '已回复',
        color: 'bg-green-500/20 text-green-400 border-green-500/50',
      },
    };
    const badge = badges[status] || badges.unread;
    return (
      <span className={`px-2 py-1 rounded text-xs border ${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* 顶部筛选 */}
      <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg p-4">
        <div className="flex gap-4">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'all'
                ? 'bg-[#4a5f3a] text-white'
                : 'bg-[#262a22] text-gray-400 hover:text-white'
            }`}
          >
            全部
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'unread'
                ? 'bg-[#4a5f3a] text-white'
                : 'bg-[#262a22] text-gray-400 hover:text-white'
            }`}
          >
            未读
          </button>
          <button
            onClick={() => setFilter('read')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'read'
                ? 'bg-[#4a5f3a] text-white'
                : 'bg-[#262a22] text-gray-400 hover:text-white'
            }`}
          >
            已读
          </button>
          <button
            onClick={() => setFilter('replied')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'replied'
                ? 'bg-[#4a5f3a] text-white'
                : 'bg-[#262a22] text-gray-400 hover:text-white'
            }`}
          >
            已回复
          </button>
        </div>
      </div>

      {/* 消息列表 */}
      <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">
            <div className="inline-block">
              <div className="w-8 h-8 border-2 border-gray-400 border-t-white rounded-full animate-spin"></div>
            </div>
            <p className="mt-2">加载中...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="p-8 text-center text-gray-400">暂无消息</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#2a2d27]">
                  <th className="text-left p-4 text-gray-400 font-medium">
                    姓名
                  </th>
                  <th className="text-left p-4 text-gray-400 font-medium">
                    邮箱
                  </th>
                  <th className="text-left p-4 text-gray-400 font-medium">
                    主题
                  </th>
                  <th className="text-left p-4 text-gray-400 font-medium">
                    状态
                  </th>
                  <th className="text-left p-4 text-gray-400 font-medium">
                    时间
                  </th>
                  <th className="text-left p-4 text-gray-400 font-medium">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody>
                {messages.map((msg) => (
                  <tr
                    key={msg.id}
                    className="border-b border-[#2a2d27] hover:bg-[#262a22] transition-colors"
                  >
                    <td className="p-4 text-white">{msg.name}</td>
                    <td className="p-4 text-gray-400">{msg.email}</td>
                    <td className="p-4 text-white">{msg.subject}</td>
                    <td className="p-4">{getStatusBadge(msg.status)}</td>
                    <td className="p-4 text-gray-400 text-sm">
                      {formatDate(msg.created_at)}
                    </td>
                    <td className="p-4">
                      <button
                        onClick={() => handleViewMessage(msg.id)}
                        className="px-3 py-1 bg-[#4a5f3a] hover:bg-[#5a7f4a] text-white rounded text-sm transition-colors"
                      >
                        查看
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 分页 */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-[#2a2d27] flex justify-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-[#262a22] text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              上一页
            </button>
            <span className="px-4 py-2 text-white">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-[#262a22] text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              下一页
            </button>
          </div>
        )}
      </div>

      {/* 消息详情模态框 */}
      {showDetail && selectedMessage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6 border-b border-[#2a2d27] flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">消息详情</h3>
              <button
                onClick={() => setShowDetail(false)}
                className="text-gray-400 hover:text-white"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="currentColor"
                  viewBox="0 0 256 256"
                >
                  <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path>
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  姓名
                </label>
                <p className="text-white">{selectedMessage.name}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  邮箱
                </label>
                <p className="text-white">{selectedMessage.email}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  主题
                </label>
                <p className="text-white">{selectedMessage.subject}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  消息内容
                </label>
                <div className="bg-[#262a22] border border-[#4b5445] rounded-lg p-4 text-white whitespace-pre-wrap">
                  {selectedMessage.message}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  状态
                </label>
                <div className="flex gap-2">
                  {getStatusBadge(selectedMessage.status)}
                  <span className="text-gray-400 text-sm">
                    {formatDate(selectedMessage.created_at)}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  管理员备注
                </label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="添加备注..."
                  className="w-full px-4 py-3 rounded-lg bg-[#262a22] border-2 border-[#4b5445] text-white placeholder-gray-500 focus:outline-none focus:border-[#5a6f4a] transition-colors resize-none"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  回复邮件
                  <span className="ml-2 text-xs text-gray-500">
                    (将从 sales@metalboxmodels.com 发送)
                  </span>
                </label>
                <textarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder="输入回复内容..."
                  className="w-full px-4 py-3 rounded-lg bg-[#262a22] border-2 border-[#4b5445] text-white placeholder-gray-500 focus:outline-none focus:border-[#5a6f4a] transition-colors resize-none"
                  rows={6}
                  disabled={isSending}
                />
                <p className="mt-1 text-xs text-gray-500">
                  邮件将包含用户的原始消息，签名为 Jason Jin (CEO)
                </p>
              </div>

              {/* 附件上传 */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  附件
                  <span className="ml-2 text-xs text-gray-500">
                    (最多5个，每个不超过10MB)
                  </span>
                </label>

                <div className="flex items-center gap-3">
                  <label className="flex-shrink-0 px-4 py-2 bg-[#262a22] hover:bg-[#4b5445] text-white rounded cursor-pointer transition-colors border border-[#4b5445]">
                    <input
                      type="file"
                      multiple
                      onChange={handleFileChange}
                      disabled={isSending || attachments.length >= 5}
                      accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
                      className="hidden"
                    />
                    选择文件
                  </label>
                  <span className="text-sm text-gray-500">
                    {attachments.length > 0
                      ? `已选择 ${attachments.length} 个文件`
                      : '未选择文件'}
                  </span>
                </div>

                {/* 附件列表 */}
                {attachments.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {attachments.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-[#262a22] border border-[#4b5445] rounded px-3 py-2"
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            viewBox="0 0 256 256"
                            className="text-gray-400 flex-shrink-0"
                          >
                            <path d="M213.66,82.34l-56-56A8,8,0,0,0,152,24H56A16,16,0,0,0,40,40V216a16,16,0,0,0,16,16H200a16,16,0,0,0,16-16V88A8,8,0,0,0,213.66,82.34ZM160,51.31,188.69,80H160ZM200,216H56V40h88V88a8,8,0,0,0,8,8h48V216Z"></path>
                          </svg>
                          <span className="text-white text-sm truncate">
                            {file.name}
                          </span>
                          <span className="text-gray-500 text-xs flex-shrink-0">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                        <button
                          onClick={() => removeAttachment(index)}
                          disabled={isSending}
                          className="ml-2 text-red-400 hover:text-red-300 disabled:opacity-50 flex-shrink-0"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            viewBox="0 0 256 256"
                          >
                            <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path>
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleSendReply}
                  disabled={isSending || !replyContent.trim()}
                  className="px-6 py-2 bg-[#4a5f3a] hover:bg-[#5a7f4a] disabled:bg-[#4a5f3a]/50 disabled:cursor-not-allowed text-white rounded transition-colors flex items-center gap-2"
                >
                  {isSending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      发送中...
                    </>
                  ) : (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        viewBox="0 0 256 256"
                      >
                        <path d="M227.32,28.68a16,16,0,0,0-15.66-4.08l-.15,0L19.57,82.84a16,16,0,0,0-2.49,29.8L102,154l41.3,84.87A15.86,15.86,0,0,0,157.74,248q.69,0,1.38-.06a15.88,15.88,0,0,0,14-11.51l58.2-191.94c0-.05,0-.1,0-.15A16,16,0,0,0,227.32,28.68ZM157.83,231.85l-.05.14L118.42,148.9l47.24-47.25a8,8,0,0,0-11.31-11.31L107.1,137.58,24,98.22l.14,0L216,40Z"></path>
                      </svg>
                      发送回复
                    </>
                  )}
                </button>
                <button
                  onClick={() => handleUpdateStatus('read')}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                >
                  标记为已读
                </button>
                <button
                  onClick={() => handleUpdateStatus('replied')}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                >
                  标记为已回复
                </button>
                <button
                  onClick={() => handleDelete(selectedMessage.id)}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors ml-auto"
                >
                  删除
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast 提示 */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* 确认对话框 */}
      {confirmDialog && (
        <ConfirmDialog
          message={confirmDialog.message}
          type={confirmDialog.type}
          onConfirm={confirmDialog.onConfirm}
          onCancel={confirmDialog.onCancel}
        />
      )}
    </div>
  );
};

export default ContactMessages;
